// Decides whether applying a waiting service worker *right now* would destroy
// user work. Pure over its inputs — no clock, no storage, no React.

export type PendingWork = "unknown" | "idle" | "busy";
export type FormDirtiness = "unknown" | "clean" | "dirty";
export type ApplyDecision =
	| "apply-now"
	| "defer-user-busy"
	| "defer-work-pending";

export interface ApplyContext {
	pendingWork: PendingWork;
	formDirtiness: FormDirtiness;
	deferredSinceMs: number | null;
	now: number;
}

/** How long a deferral may stand before the pending-work condition relaxes. */
export const APPLY_ESCALATION_MS = 30 * 60_000;

/**
 * Both safety inputs are tri-state on purpose: `unknown` is the value before a
 * snapshot has been read, and a boolean default would be *fail-open* — the one
 * value that permits a reload. `unknown` never permits an apply on the default
 * path.
 *
 * Escalation relaxes only the pending-work condition, and it relaxes **`busy`**,
 * not `unknown`. Do not "restore" a `!== "busy"` test here: since `idle` is
 * already permitted un-escalated, that formula would unlock only `unknown` and
 * so would relax nothing for the case the deadline exists for — the offline
 * field user holding a persistently non-empty queue. Relaxing `busy` is safe
 * because queued work lives in IndexedDB and survives a reload; the guard
 * against a mid-write race is `appReload` awaiting `awaitPendingWorkSettled()`
 * before its final synchronous re-check, not this condition.
 *
 * The `formDirtiness === "clean"` condition never relaxes: in-memory form state
 * is the only thing a reload can destroy, and there is no elapsed time after
 * which the app is entitled to discard a half-filled form. Visibility is
 * deliberately not an input — a backgrounded window can hold a dirty form.
 */
export function decideUpdateApply(context: ApplyContext): ApplyDecision {
	const escalated =
		context.deferredSinceMs !== null &&
		context.now - context.deferredSinceMs >= APPLY_ESCALATION_MS;

	const workAllowsApply = escalated
		? context.pendingWork !== "unknown"
		: context.pendingWork === "idle";

	if (!workAllowsApply) return "defer-work-pending";
	if (context.formDirtiness !== "clean") return "defer-user-busy";
	return "apply-now";
}
